/**
 * HomePage.tsx
 * ------------
 * 메인 페이지 — 배너를 DB API에서 조회 (목업 데이터 제거)
 */

import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ScrollToTopButton from "../components/ui/ScrollToTopButton";
import { useIsMobile } from "../hooks/useIsMobile";
import { shouldIgnoreClick } from "../utils/clickGuard";
import { useBanners, useEventBanners, useSilverBanners, ApiBanner } from "../hooks/useBusinessData";
import { isLoggedIn as isLoggedInFn } from "../hooks/useAuth";
import { PageLayout } from "../components/layout";
import EmergencyNotice from "../components/sections/EmergencyNotice";
import MainMenu from "../components/sections/MainMenu";
import LeftSidebar from "../components/sections/LeftSidebar";
import RightSidebar from "../components/sections/RightSidebar";
import BannerSlider from "../components/sections/BannerSlider";
import StandardGrid from "../components/sections/StandardGrid";
import LoginPopup from "../components/ui/LoginPopup";
import SignupPopup from "../components/ui/SignupPopup";
import { HomeBannersSkeleton } from "../components/ui/Skeleton";
import { layoutConfig } from "../data";
import { Banner } from "../types";
import { loadSearchIndex, searchBanners, SearchItem, reloadCatalogIfStale, forceReloadCatalog } from "../utils/searchEngine";
import { bannerImgAttrs, CTX_SIZES } from "../utils/bannerImg";

/** DB ApiBanner → 프론트 Banner 타입 변환 */
// 등급 매핑: 4=추천(promoted), 5=이벤트(event)는 별도 섹션, 6=일반(standard) 그리드
const GRADE_TO_TIER: Record<number, string> = { 1: "vip", 2: "platinum", 3: "gold", 4: "promoted", 5: "event", 6: "standard" };

function toBanner(b: ApiBanner, tier?: string): Banner {
  return {
    advertiserKey: `banner-${b.id}`,
    categoryKey: b.category_key || "",
    countryKey: b.country_key || "",
    cityKey: b.city_key || "",
    tier: (tier || GRADE_TO_TIER[b.banner_grade] || "standard") as Banner["tier"],
    tierIndex: b.sort_order,
    tierTotal: 0,
    title: b.title || "",
    subtitle: b.subtitle || "",
    location: b.city_key || "",
    tag: "",
    tagColor: "sky",
    imageUrl: b.image_url || "",
    imageSizes: b.image_sizes || null,
    html: b.html || "",
    bodyEditor: (b as { body_editor?: string | null }).body_editor || null,
    showTitle: !!b.show_title,
    showSubtitle: !!b.show_subtitle,
    showCity: !!b.show_city,
    showCommentCount: !!b.show_comment_count,
    commentCount: b.comment_count || 0,
    businessType: b.business_type ?? null,
    businessName: b.business_name ?? null,
    address: b.address ?? null,
    businessHours: b.business_hours ?? null,
    phoneNumber: b.phone_number ?? null,
    phoneNumberSecondary: b.phone_number_secondary ?? null,
    website: b.website ?? null,
    sns: b.sns ?? null,
    kakaoUrl: b.kakao_url ?? null,
    telegramUrl: b.telegram_url ?? null,
    showFavoriteCount: !!b.show_favorite_count,
    favoriteCount: b.favorite_count || 0,
    isConfirmed: (b as { is_confirmed?: boolean }).is_confirmed !== false,
  };
}

/** catalog 항목(단축키) → 카드용 Banner. 연락처/html 본문은 catalog 에 없음 → 카드 클릭 시 상세 fetch 로 채움 */
function catalogToBanner(c: SearchItem, tier?: string): Banner {
  return {
    advertiserKey: `banner-${c.id}`,
    categoryKey: c.catk || "",
    countryKey: c.cok || "",
    cityKey: c.ck || "",
    tier: (tier || GRADE_TO_TIER[c.g || 6] || "standard") as Banner["tier"],
    tierIndex: c.so || 0,
    tierTotal: 0,
    title: c.t || "",
    subtitle: c.s || "",
    location: c.ck || "",
    tag: "",
    tagColor: "sky",
    imageUrl: c.img || "",
    imageSizes: c.imgs || null,
    html: "",
    bodyEditor: null,
    showTitle: !!c.st,
    showSubtitle: !!c.ss,
    showCity: !!c.scy,
    showCommentCount: !!c.scc,
    commentCount: c.cc || 0,
    businessType: c.bt ?? null,
    businessName: c.bn ?? null,
    address: null,
    businessHours: null,
    phoneNumber: null,
    phoneNumberSecondary: null,
    website: null,
    sns: null,
    kakaoUrl: null,
    telegramUrl: null,
    showFavoriteCount: !!c.sf,
    favoriteCount: c.fc || 0,
    isConfirmed: c.cf !== false,
  };
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  // 윈도잉(WindowedGrid) 스크롤 컨테이너 — ref.current 는 첫 렌더 null 이라 state 로도 element 전달.
  // useCallback 으로 ref 콜백 고정(인라인이면 매 렌더 재생성되어 진동).
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  const setScrollRef = useCallback((el: HTMLDivElement | null) => {
    scrollRef.current = el;
    setScrollEl((prev) => (prev === el ? prev : el));
  }, []);

  // #497 당겨서 새로고침 — 네이티브 러버밴드 대신 touchmove+preventDefault 로 직접 구현
  // (실기기 검증 완료: 최상단에서 아래로 당길 때만 가로채, 위로 스와이프하는 일반 스크롤은 그대로 통과)
  // 핸들러 내부는 ref 로만 상태를 읽고 쓴다 — state 를 deps 에 넣으면 드래그 중 매 프레임 effect 가
  // 재실행되어 touchstart 에서 잡은 startY 가 초기화되며 첫 프레임 이후 제스처가 끊기는 버그가 있었음.
  const PULL_THRESHOLD = 64;
  const PULL_MAX = 90;
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullDraggingRef = useRef(false);
  const refreshingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  useEffect(() => {
    const el = scrollEl;
    if (!el) return;
    let startY: number | null = null;
    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      startY = e.touches[0].clientY;
      pullDraggingRef.current = false;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (startY == null || refreshingRef.current) return;
      const delta = e.touches[0].clientY - startY;
      if (delta <= 0 || el.scrollTop > 0) {
        pullDraggingRef.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }
      pullDraggingRef.current = true;
      e.preventDefault();
      const next = Math.min(delta * 0.5, PULL_MAX);
      pullDistanceRef.current = next;
      setPullDistance(next);
    };
    const onTouchEnd = () => {
      if (pullDraggingRef.current && pullDistanceRef.current >= PULL_THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        const started = Date.now();
        forceReloadCatalog(i18n.language).then((next) => {
          setCatalog(next);
          const elapsed = Date.now() - started;
          setTimeout(() => {
            refreshingRef.current = false;
            setRefreshing(false);
            pullDistanceRef.current = 0;
            setPullDistance(0);
          }, Math.max(0, 500 - elapsed));
        }).catch(() => {
          refreshingRef.current = false;
          setRefreshing(false);
          pullDistanceRef.current = 0;
          setPullDistance(0);
        });
      } else {
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
      startY = null;
      pullDraggingRef.current = false;
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [scrollEl, i18n.language]);
  // 요구사항 #552: 나라선택 필리핀 고정 — 저장된 favorite_country_id 와 무관하게 항상 PH(1).
  const savedCountryId = 1;
  const COUNTRY_ID_KEY: Record<number, string> = { 1: "ph", 2: "vn", 3: "kh", 4: "kr", 5: "us" };
  const [activeCategoryKey, setActiveCategoryKey] = useState<string | null>(null);
  const [activeCountryKey, setActiveCountryKey] = useState<string>(COUNTRY_ID_KEY[savedCountryId] || "ph");
  const [activeCityKey, setActiveCityKey] = useState<string | null>(null);
  // ID 기반 필터 (배너 API용) — 도시는 항상 All Cities로 시작
  const [activeCountryId, setActiveCountryId] = useState<number>(savedCountryId);
  const [activeCityId, setActiveCityId] = useState<number | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const isMobile = useIsMobile();

  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showSignupPopup, setShowSignupPopup] = useState(false);

  // 모든 배너 16:9 고정 — 등급별 비율 fetch 제거
  // 선호 국가/언어 변경 시 DB 저장
  const savePreference = (countryId?: number, cityId?: number | null) => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    const API_URL = process.env.REACT_APP_API_URL || "https://api.trippang.com";
    const body: Record<string, number | null> = {};
    if (countryId !== undefined) body.favorite_country_id = countryId;
    if (cityId !== undefined) body.favorite_city_id = cityId;
    fetch(`${API_URL}/user/profile/preference`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    }).catch(() => {});
  };

  // 검색 쿼리 (URL → 로컬 state)
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSearchTag, setActiveSearchTag] = useState<string | null>(searchParams.get("q") || null);

  // URL 변경 시 동기화
  // 검색 시작 시 카테고리 필터 자동 해제 — 키워드가 특정 카테고리 전용일 때 (예: KTV/JTV 는 Nightlife 만)
  // 카테고리 미스매치로 빈 결과 나오는 UX 문제 회피
  useEffect(() => {
    const q = searchParams.get("q") || null;
    setActiveSearchTag(q);
    if (q && activeCategoryId !== null) {
      setActiveCategoryKey(null);
      setActiveCategoryId(null);
    }
  }, [searchParams]);  // eslint-disable-line react-hooks/exhaustive-deps

  // 전역 폰트 정책 fetch — 등급(VIP/PLATINUM/GOLD/SILVER) × 화면(PC/모바일) × 타이틀/부타이틀 = 16 값
  // CSS var 16개 + 등급별 활성(--banner-title-vip, --banner-subtitle-vip 등) 동시 설정
  // viewport 직접 검사 (matchMedia) 후 PC/모바일 단일 var 16→8 축약 — CSS media query 신뢰 안 함
  useEffect(() => {
    type FontSet = { title_pc: number; title_mobile: number; subtitle_pc: number; subtitle_mobile: number };
    type FontGroups = { VIP: FontSet; PLATINUM: FontSet; GOLD: FontSet; SILVER: FontSet };
    let fonts: FontGroups | null = null;

    const applyFonts = () => {
      if (!fonts) return;
      const isPc = window.matchMedia("(min-width: 768px)").matches;
      const root = document.documentElement;
      (Object.keys(fonts) as (keyof FontGroups)[]).forEach((g) => {
        const f = (fonts as FontGroups)[g];
        const gl = g.toLowerCase();
        root.style.setProperty(`--banner-title-${gl}`, `${isPc ? f.title_pc : f.title_mobile}px`);
        root.style.setProperty(`--banner-subtitle-${gl}`, `${isPc ? f.subtitle_pc : f.subtitle_mobile}px`);
        root.style.setProperty(`--banner-title-${gl}-pc`, `${f.title_pc}px`);
        root.style.setProperty(`--banner-title-${gl}-mobile`, `${f.title_mobile}px`);
        root.style.setProperty(`--banner-subtitle-${gl}-pc`, `${f.subtitle_pc}px`);
        root.style.setProperty(`--banner-subtitle-${gl}-mobile`, `${f.subtitle_mobile}px`);
      });
      // 레거시 단일 var = VIP 값 (기존 컴포넌트 호환)
      const v = (fonts as FontGroups).VIP;
      root.style.setProperty("--banner-title", `${isPc ? v.title_pc : v.title_mobile}px`);
      root.style.setProperty("--banner-subtitle", `${isPc ? v.subtitle_pc : v.subtitle_mobile}px`);
    };

    (async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || "https://api.trippang.com";
        const r = await fetch(`${API_URL}/public/business/banner-fonts`);
        if (!r.ok) return;
        const d = await r.json();
        if (d && typeof d === "object" && d.VIP) {
          fonts = { VIP: d.VIP, PLATINUM: d.PLATINUM, GOLD: d.GOLD, SILVER: d.SILVER };
          applyFonts();
        }
      } catch {}
    })();

    // viewport 변경(리사이즈/회전) 시 적절한 값 재적용
    window.addEventListener("resize", applyFonts);
    return () => window.removeEventListener("resize", applyFonts);
  }, []);

  // 검색 중에는 선택된 "국가" 스코프 유지 + 도시/카테고리는 무시(국가 전체에서 검색).
  // Header 검색 카운트도 동일 국가로 스코프 → 카운트=결과 일치
  const sActive = !!activeSearchTag;
  const fCountry = activeCountryId;
  const fCity = sActive ? null : activeCityId;
  const fCategory = sActive ? null : activeCategoryId;

  // ─── 전체 전환: catalog 단일 페이로드로 표시+검색이 기본. kill-switch: localStorage use_catalog='0' 이면 현행 훅으로 복귀 ───
  const USE_CATALOG = typeof window === "undefined" ? true : localStorage.getItem("use_catalog") !== "0";

  // 기존 4개 훅 — 플래그 OFF(kill-switch) 시에만 fetch. catalog 모드면 enabled=false 로 중복 호출 스킵.
  const [apiBanners, bannersLoading] = useBanners(fCountry, fCity, fCategory, activeSearchTag, 4, !USE_CATALOG);
  const [apiEventBanners, eventLoading] = useEventBanners(fCountry, fCity, activeSearchTag, !USE_CATALOG);
  const { items: apiSilver, total: hookSilverTotal, loadMore: hookSilverLoadMore } = useSilverBanners(fCountry, fCity, fCategory, activeSearchTag, !USE_CATALOG);

  // catalog 로드 — 플래그 ON 일 때만 실제 fetch (OFF 면 네트워크/메모리 0)
  const [catalog, setCatalog] = useState<SearchItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState<boolean>(USE_CATALOG);
  useEffect(() => {
    if (!USE_CATALOG) return;
    let alive = true;
    setCatalogLoading(true);
    loadSearchIndex(i18n.language).then((d) => { if (alive) { setCatalog(d); setCatalogLoading(false); } });
    return () => { alive = false; };
  }, [USE_CATALOG, i18n.language]);

  // #413 자동 순서 갱신 — 30분 버킷이 바뀌면 새 순서 catalog 재요청. 새 순서 썸네일을 먼저
  // 프리로드(브라우저 캐시)한 뒤 setCatalog → 재렌더 시 이미지 깜빡임 0. (1분마다 + 탭 복귀 시 체크)
  useEffect(() => {
    if (!USE_CATALOG) return;
    let alive = true;
    let busy = false;
    const apply = async () => {
      if (busy) return;
      busy = true;
      try {
        const next = await reloadCatalogIfStale(i18n.language);
        if (!alive || !next) return;
        // 카드와 동일한 bannerImgAttrs 로 같은 이미지 변형을 미리 캐시 → 적용 시 재로드 0
        await Promise.race([
          Promise.all(next.map((c) => new Promise<void>((res) => {
            const a = bannerImgAttrs(c.img || "", c.imgs, CTX_SIZES.card, 200);
            if (!a.src) return res();
            const im = new Image();
            if (a.sizes) im.sizes = a.sizes;
            if (a.srcSet) im.srcset = a.srcSet;
            im.onload = im.onerror = () => res();
            im.src = a.src;
          }))),
          new Promise<void>((res) => setTimeout(res, 2500)),
        ]);
        if (alive) setCatalog(next);  // 이미지 캐시 완료 후 순서 적용 → 깜빡임 0
      } finally {
        busy = false;
      }
    };
    const id = window.setInterval(apply, 60000);  // 1분마다 30분 경계 체크
    const onVis = () => { if (document.visibilityState === "visible") apply(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; window.clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [USE_CATALOG, i18n.language]);

  // 점프 실행 직후 즉시 순서 반영 — 실버 영역이 order_at desc 이므로 catalog 를 버킷 무관 강제 재조회
  useEffect(() => {
    if (!USE_CATALOG) return;
    const onJumpDone = () => { forceReloadCatalog(i18n.language).then(setCatalog); };
    window.addEventListener("trippang:jump-done", onJumpDone);
    return () => window.removeEventListener("trippang:jump-done", onJumpDone);
  }, [USE_CATALOG, i18n.language]);

  // 필터/검색 바뀌면 실버 더보기 리셋
  const [silverVisible, setSilverVisible] = useState(30);
  useEffect(() => { setSilverVisible(30); }, [activeSearchTag, fCountry, fCity, fCategory]);

  // catalog → 등급 그룹 파생 (검색이면 searchBanners[승인분만], 아니면 국가/도시/카테고리 필터[활성 전체])
  const catDerived = useMemo(() => {
    if (!USE_CATALOG) return null;
    let items: SearchItem[];
    if (sActive && activeSearchTag) {
      items = searchBanners(activeSearchTag, catalog, 99999, fCountry || undefined);
    } else {
      items = catalog.filter((c) =>
        (!fCountry || c.c === fCountry) &&
        (!fCity || c.cid === fCity) &&
        (!fCategory || c.catid === fCategory));
    }
    return {
      std: items.filter((c) => (c.g || 6) >= 1 && (c.g || 6) <= 4).map((c) => catalogToBanner(c)),
      ev: items.filter((c) => c.g === 5).map((c) => catalogToBanner(c, "event")),
      silverAll: items.filter((c) => c.g === 6).map((c) => catalogToBanner(c, "standard")),
    };
  }, [USE_CATALOG, catalog, sActive, activeSearchTag, fCountry, fCity, fCategory]);

  // 표시 변수 — 플래그에 따라 catalog 파생 vs 기존 훅 선택
  const banners = useMemo(() => USE_CATALOG ? (catDerived?.std || []) : apiBanners.map((b) => toBanner(b)), [USE_CATALOG, catDerived, apiBanners]);
  const eventBanners = useMemo(() => USE_CATALOG ? (catDerived?.ev || []) : apiEventBanners.map((b) => toBanner(b, "event")), [USE_CATALOG, catDerived, apiEventBanners]);
  const silverBanners = useMemo(() => USE_CATALOG ? (catDerived?.silverAll || []).slice(0, silverVisible) : apiSilver.map((b) => toBanner(b, "standard")), [USE_CATALOG, catDerived, silverVisible, apiSilver]);
  const silverTotal = USE_CATALOG ? (catDerived?.silverAll.length || 0) : hookSilverTotal;
  const silverLoadMore = USE_CATALOG ? (() => setSilverVisible((v) => v + 30)) : hookSilverLoadMore;
  const isInitialLoading = USE_CATALOG ? catalogLoading : (bannersLoading || eventLoading);

  // require-login 이벤트는 Header 가 전역 처리 (모든 페이지에서 동작)
  // HomePage 의 LoginPopup 은 Header 의 setActivePopup("login") 와 중복되므로 dispatch 사용

  // #449 3차: 베너 상세는 팝업 state가 아니라 /banner/:id 라우트(background location 패턴)로
  // 오픈 — 안드로이드 시스템 뒤로가기가 일반 페이지 전환과 동일하게 동작하게 됨.
  const navigate = useNavigate();
  const location = useLocation();
  const openBanner = useCallback((bannerId: number) => {
    navigate(`/banner/${bannerId}`, { state: { backgroundLocation: location } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // 알림/검색 클릭 → 해당 배너 상세로 이동 (bannerId만 있으면 됨 — 상세는 라우트가 직접 fetch)
  useEffect(() => {
    const handleOpenBanner = (e: Event) => {
      const bannerId = (e as CustomEvent).detail?.bannerId;
      if (bannerId) openBanner(bannerId);
    };
    window.addEventListener("open-banner", handleOpenBanner);
    // 공유 URL(?banner=ID) 하위호환 — 새 라우트(/banner/ID)로 치환(배경 없이 진입 → 닫으면 홈)
    const sharedId = Number(new URLSearchParams(window.location.search).get("banner"));
    if (sharedId > 0) {
      navigate(`/banner/${sharedId}`, { replace: true });
    }
    return () => window.removeEventListener("open-banner", handleOpenBanner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // VIP/플래티넘/골드 — 서버 5분 버킷 셔플(기본)에 더해, 앱이 백그라운드에서
  // 다시 활성화(visibilitychange→visible)될 때마다 이 3개 그룹만 클라이언트에서
  // 한 번 더 무작위 재배치. 서버/캐싱(ETag)은 그대로 두고 화면에서만 추가로 섞는 방식 —
  // 최초 진입 시엔 activeShuffleTick=0 이라 서버가 준 순서 그대로 표시됨.
  const [activeShuffleTick, setActiveShuffleTick] = useState(0);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") setActiveShuffleTick((n) => n + 1); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  const shuffleClientSide = <T,>(arr: T[]): T[] => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // 등급별 분류
  const vipBannersBase = banners.filter((b) => b.tier === "vip");
  const platBannersBase = banners.filter((b) => b.tier === "platinum");
  const goldBannersBase = banners.filter((b) => b.tier === "gold");
  const vipBanners = useMemo(
    () => (activeShuffleTick > 0 ? shuffleClientSide(vipBannersBase) : vipBannersBase),
    [vipBannersBase, activeShuffleTick] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const platBanners = useMemo(
    () => (activeShuffleTick > 0 ? shuffleClientSide(platBannersBase) : platBannersBase),
    [platBannersBase, activeShuffleTick] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const goldBanners = useMemo(
    () => (activeShuffleTick > 0 ? shuffleClientSide(goldBannersBase) : goldBannersBase),
    [goldBannersBase, activeShuffleTick] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const promotedBanners = banners.filter((b) => b.tier === "promoted");  // 등급 4 추천
  // 실버(등급 6)는 useSilverBanners(페이지네이션)로 별도 — banners(maxGrade=4)엔 없음

  // 배너 클릭: 로그인 필수(비로그인 시 Header 의 require-login 리스너가 LoginPopup 오픈)
  const handleBannerClick = (advertiserKey: string) => {
    if (shouldIgnoreClick()) return; // dropdown 옵션 클릭 직후 ghost 호출 차단
    if (!isLoggedInFn()) {
      window.dispatchEvent(new Event("require-login"));
      return;
    }
    const m = advertiserKey.match(/^banner-(\d+)$/);
    if (!m) return;
    openBanner(parseInt(m[1], 10));
  };

  // 사용자가 도시/카테고리 선택 시 카드에 해당 라벨 중복 표시 안 함
  const showCity = !activeCityKey;
  const showCategory = !activeCategoryKey;
  const cfg = layoutConfig;

  return (
    <PageLayout
      notice={<EmergencyNotice />}
    >
      <div className="flex flex-1 min-h-0 flex-col">

        {/* #497 당겨서 새로고침 인디케이터 — 헤더 바로 아래, 당긴 만큼만 노출 */}
        {(pullDistance > 0 || refreshing) && (
          <div
            className="w-full flex items-center justify-center overflow-hidden pointer-events-none"
            style={{
              height: refreshing ? 40 : pullDistance,
              transition: pullDraggingRef.current ? "none" : "height 0.2s ease-out",
            }}
          >
            <div
              className="h-6 w-6 rounded-full border-2 border-sky-400 border-t-transparent animate-spin"
              style={{ opacity: refreshing ? 1 : Math.min(pullDistance / PULL_THRESHOLD, 1) }}
            />
          </div>
        )}

        {/* 모바일 전용 가로 메뉴바 (md:hidden) — 데스크탑은 LeftSidebar 가 담당 */}
        <MainMenu
          activeCategoryKey={activeCategoryKey}
          activeCountryKey={activeCountryKey}
          activeCityKey={activeCityKey}
          onCategoryChange={(key, id) => {
            if (!isLoggedInFn()) { window.dispatchEvent(new Event("require-login")); return; }
            setActiveCategoryKey(key); setActiveCategoryId(id ?? null); setActiveSearchTag(null); searchParams.delete("q"); setSearchParams(searchParams, { replace: true });
          }}
          onCountryChange={(key, id) => {
            if (!isLoggedInFn()) { window.dispatchEvent(new Event("require-login")); return; }
            setActiveCountryKey(key); setActiveCountryId(id); localStorage.setItem("favorite_country_id", String(id)); savePreference(id); setActiveSearchTag(null); searchParams.delete("q"); setSearchParams(searchParams, { replace: true });
          }}
          onCityChange={(key, id) => {
            if (!isLoggedInFn()) { window.dispatchEvent(new Event("require-login")); return; }
            setActiveCityKey(key); setActiveCityId(id ?? null); if (id) { localStorage.setItem("favorite_city_id", String(id)); savePreference(undefined, id); } else { localStorage.removeItem("favorite_city_id"); savePreference(undefined, null); } setActiveSearchTag(null); searchParams.delete("q"); setSearchParams(searchParams, { replace: true });
          }}
        />

        {/* 데스크탑: 좌측 사이드바 + 우측 메인 (md:flex-row), 모바일: 단일 컬럼 */}
        <div className="flex flex-1 min-h-0">

        {/* 데스크탑 전용 좌측 사이드바 (hidden md:flex) */}
        <LeftSidebar
          activeCategoryKey={activeCategoryKey}
          activeCountryKey={activeCountryKey}
          activeCityKey={activeCityKey}
          onCategoryChange={(key, id) => {
            if (!isLoggedInFn()) { window.dispatchEvent(new Event("require-login")); return; }
            setActiveCategoryKey(key); setActiveCategoryId(id ?? null); setActiveSearchTag(null); searchParams.delete("q"); setSearchParams(searchParams, { replace: true });
          }}
          onCountryChange={(key, id) => {
            if (!isLoggedInFn()) { window.dispatchEvent(new Event("require-login")); return; }
            setActiveCountryKey(key); setActiveCountryId(id); localStorage.setItem("favorite_country_id", String(id)); savePreference(id); setActiveSearchTag(null); searchParams.delete("q"); setSearchParams(searchParams, { replace: true });
          }}
          onCityChange={(key, id) => {
            if (!isLoggedInFn()) { window.dispatchEvent(new Event("require-login")); return; }
            setActiveCityKey(key); setActiveCityId(id ?? null); if (id) { localStorage.setItem("favorite_city_id", String(id)); savePreference(undefined, id); } else { localStorage.removeItem("favorite_city_id"); savePreference(undefined, null); } setActiveSearchTag(null); searchParams.delete("q"); setSearchParams(searchParams, { replace: true });
          }}
        />

        {/* 스크롤 영역 — 매우 약한 그레이 배경 (slate-50) */}
        <div ref={setScrollRef} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-slate-50 pb-safe-bottom [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="mx-auto w-full max-w-[var(--content-width)] flex flex-col gap-[7.5px] pt-[7.5px] sm:gap-[15px] sm:pt-[15px]">

            {/* 검색 모드 indicator + 해제 버튼 */}
            {activeSearchTag && (
              <div className="mx-3 sm:mx-0 flex items-center justify-between gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs sm:text-sm">
                <span className="text-slate-700 truncate">
                  <span className="text-sky-600 font-semibold">"{activeSearchTag}"</span>
                  {" "}{t("search.resultCount", { count: banners.length + eventBanners.length + silverTotal, defaultValue: `검색 결과 ${banners.length + eventBanners.length + silverTotal}건` })}
                </span>
                <button
                  onClick={() => { setActiveSearchTag(null); searchParams.delete("q"); setSearchParams(searchParams, { replace: true }); }}
                  className="shrink-0 rounded px-2 py-0.5 text-sky-600 hover:bg-sky-100 transition-colors"
                  aria-label="clear search"
                >
                  ✕
                </button>
              </div>
            )}

            {/* 초기 로딩: 스켈레톤 */}
            {isInitialLoading && <HomeBannersSkeleton />}

            {/* 로딩 끝났는데 데이터 없음: 안내 메시지 */}
            {!isInitialLoading && banners.length === 0 && eventBanners.length === 0 && silverTotal === 0 && (
              <div className="py-20 text-center text-slate-500">
                <p className="text-lg">{activeSearchTag ? (t("search.noResult", { defaultValue: "검색 결과 없음" })) : (t("grid.noBanners") || "No banners available")}</p>
                <p className="mt-2 text-sm">{activeSearchTag ? (t("search.tryAnother", { defaultValue: "다른 검색어로 시도해 주세요" })) : (t("grid.noBannersDesc") || "Banners will appear here when registered by advertisers.")}</p>
              </div>
            )}

            {/* VIP 배너 (등급 1) — #375-2: 배경 전폭 하늘색(파랑), 배너 원래 크기.
                스크롤 영역(328) 스크롤바 숨김으로 우측 8px 갭 제거 → inset-x-0 만으로 좌우 끝까지 (vw 미사용 → PWA 쏠림 없음) */}
            {cfg.vip.visible && vipBanners.length > 0 && (
              <div className="relative py-2">
                <div aria-hidden className="absolute inset-x-0 bottom-0 -top-[7.5px] bg-white md:-top-[15px]" style={{ zIndex: 0 }} />
                <div className="relative" style={{ zIndex: 1 }}>
                  <BannerSlider
                    banners={vipBanners}
                    height={cfg.vip.height}
                    intervalMs={cfg.vip.intervalMs}
                    columns={1}
                    showCity={showCity}
                    showCategory={showCategory}
                    isMobile={isMobile}
                    showArrows={true}
                    onBannerClick={handleBannerClick}
                  />
                </div>
              </div>
            )}

            {/* 플래티넘 배너 (등급 2) — 요구사항 #277-4: 모든 디바이스에서 자동 슬라이드 (애니메이션 효과 복원) */}
            {cfg.platinum.visible && platBanners.length > 0 && (
              <div>
                <BannerSlider
                  banners={platBanners}
                  height={cfg.platinum.height}
                  intervalMs={cfg.platinum.intervalMs}
                  columns={2}
                  showCity={showCity}
                  showCategory={showCategory}
                  isMobile={isMobile}
                  autoSlide={true}
                  showArrows={true}
                  onBannerClick={handleBannerClick}
                />
              </div>
            )}

            {/* 골드 배너 (등급 3) — 자동 carousel + 터치 시 자동 중지 + 자유 드래그 (최신 요구사항) */}
            {cfg.gold.visible && goldBanners.length > 0 && (
              <div>
                <BannerSlider
                  banners={goldBanners}
                  height={cfg.gold.height}
                  intervalMs={cfg.gold.intervalMs}
                  columns={isMobile ? 2.5 : 3}
                  showCity={showCity}
                  showCategory={showCategory}
                  isMobile={isMobile}
                  autoSlide={false}
                  showArrows={true}
                  onBannerClick={handleBannerClick}
                />
              </div>
            )}

            {/* 추천 배너 (등급 4) — 골드 아래. 개수 0이면 영역 제거 */}
            {promotedBanners.length > 0 && (
              <div className="overflow-hidden">
                <h2 className="px-[3.75px] sm:px-[7.5px] mb-1 text-sm font-bold text-slate-700">{t("section.promoted") || "추천"}</h2>
                <StandardGrid
                  banners={promotedBanners}
                  showCity={showCity}
                  showCategory={showCategory}
                />
              </div>
            )}

            {/* 이벤트 배너 (등급 5) — 추천 아래. 개수 0이면 영역 제거 */}
            {eventBanners.length > 0 && (
              <div className="overflow-hidden">
                <h2 className="px-[3.75px] sm:px-[7.5px] mb-1 text-sm font-bold text-slate-700">{t("section.event") || "이벤트"}</h2>
                <StandardGrid
                  banners={eventBanners}
                  showCity={showCity}
                  showCategory={showCategory}
                />
              </div>
            )}

            {/* 실버 배너 (등급 6) — 최하단. order_at desc(최신 등록순, 점프 시 맨 앞) + 진짜 DOM 가상화 + 페이지네이션(무한 로드) */}
            {silverBanners.length > 0 && (
              <div className="overflow-hidden">
                <h2 className="px-[3.75px] sm:px-[7.5px] mb-1 text-sm font-bold text-slate-700">
                  {t("section.silver") || "실버"}
                  <span className="ml-1.5 text-xs font-normal text-slate-400">{silverTotal.toLocaleString()}</span>
                </h2>
                <StandardGrid
                  banners={silverBanners}
                  showCity={showCity}
                  showCategory={showCategory}
                  virtuoso
                  loadMore={silverLoadMore}
                  scrollParent={scrollEl}
                />
              </div>
            )}
          </div>
        </div>
        {/* 데스크탑 전용 우측 사이드바 — #406 위젯 (코인/환율/날씨) */}
        <RightSidebar />
        </div>{/* row container close (LeftSidebar + 스크롤 영역 + RightSidebar) */}
      </div>

      {/* 비로그인 시 로그인/회원가입 팝업 */}
      {showLoginPopup && (
        <LoginPopup
          onClose={() => setShowLoginPopup(false)}
          onSwitchToSignup={() => { setShowLoginPopup(false); setTimeout(() => setShowSignupPopup(true), 500); }}
        />
      )}
      {showSignupPopup && (
        <SignupPopup
          onClose={() => setShowSignupPopup(false)}
          onSwitchToLogin={() => { setShowSignupPopup(false); setTimeout(() => setShowLoginPopup(true), 800); }}
        />
      )}
      {/* 요구사항 #277-5 — 맨 위로 이동 floating button */}
      <ScrollToTopButton containerRef={scrollRef} />
    </PageLayout>
  );
}
